/**
 * Event handler that can subscribe to a dispatcher.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export type EventHandler<T> = (event: T) => void;


/**
 * Interface for an event dispatcher.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export interface EventDispatcherInterface<T> {
  /**
   * Registers a handler to the event dispatcher.
   *
   * @param {EventHandler<T>} handler Event handler to notify
   * @returns {void}
   */
  register(handler: EventHandler<T>): void;


  /**
   * Unregisters a handler from the event.
   *
   * @param {EventHandler<T>} handler Event handler to unregister
   * @returns {void}
   */
  unregister(handler: EventHandler<T>): void;


  /**
   * Dispatches the event and notifies all handlers.
   *
   * @param {T} event Event to dispatch
   * @returns {void}
   */
  dispatch(event: T): void;
}


/**
 * Dispatches an event to the event subscribers.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default class EventDispatcher<T> implements EventDispatcherInterface<T> {
  /**
   * Contains the event handlers.
   *
   * @type {EventHandler[]}
   * @private
   */
  private _handlers: EventHandler<T>[] = [];


  register(handler: EventHandler<T>): void {
    this._handlers.push(handler);
  }


  unregister(handler: EventHandler<T>): void {
    let i = 0;

    for (const registeredHandler of this._handlers) {
      if (registeredHandler === handler) {
        this._handlers.splice(i, 1);
      }

      i++;
    }
  }


  dispatch(event: T): void {
    for (const registeredHandler of this._handlers) {
      registeredHandler(event);
    }
  }
}
